/**
 * AK-590 — возврат из редактора контура: кому и что отдать, когда редактор
 * закрылся.
 *
 * Логика жила инлайном в `pages/map/v2.tsx` (~1900 строк, тянет maplibre-gl и
 * потому недостижима из юнит-тестов) — и именно поэтому дважды за день пропустила
 * потерю пользовательской работы: AK-577 («Готово» на незамкнутом контуре) и
 * AK-590 («Готово» стирало нарисованное поле). Здесь она чистая функция:
 * на вход — снимок фактов, на выход — намерение. Никаких сторов, карты и React.
 *
 * Ключевое понятие — ВЛАДЕЛЕЦ редактора. Редактор контура один, а открывают его
 * три разных пути:
 *   'add-field' — «+ Поле» → «Редактировать» / «Нарисовать»: контур на выходе
 *                 уходит в форму сохранения НОВОГО поля;
 *   'import'    — правка одной строки импорта: контур уходит в эту строку;
 *   null        — «Обвести поле» (traceField) и прочая правка контура
 *                 СУЩЕСТВУЮЩЕГО поля: наружу не уходит НИЧЕГО.
 *
 * Владелец — не фаза `addFieldStore`. Фаза живёт в соседнем сторе и может
 * разойтись с реальностью; а гейт по владельцу ставит тот же эффект, что и
 * открывает редактор. Без него закрытие редактора «Обвести поле» кнопкой
 * «+ Поле» (RightRail гасит редактор и включает режим добавления в одном
 * обработчике) отдало бы контур СУЩЕСТВУЮЩЕГО поля в форму создания нового —
 * то есть дубль в БД по одному клику.
 */
import type { EditorGeometry } from '@/lib/map/editor/ContourEditorController';
import type { LngLat } from '@/lib/map/editor/fieldGeojson';
import type { AddFieldPhase } from './addFieldStore';

/**
 * Кто открывал редактор — тот и получает контур на выходе.
 *
 * `'discard'` — владение снято НАМЕРЕННО: режим «+ Поле» закрыли явной
 * «Отменой» (drawing-хинт / тумблер рейла / Esc), черновик выброшен. Это НЕ
 * то же самое, что `null` («редактор никогда не был чей-то»): для null с
 * нарисованной с нуля геометрией работает спасение draw-save-as-new (AK-591),
 * а для discard любой выход — строго noop, иначе «Отмена» воскрешала бы
 * только что отменённый контур формой сохранения.
 */
export type EditorOwner = 'add-field' | 'import' | 'discard' | null;

export interface ReturnFromEditorInput {
  /** Кто открывал редактор (ref-владение, ставится в эффектах открытия). */
  owner: EditorOwner;
  /** Строка импорта, чей контур правили (для owner='import'). */
  importIndex: number | null;
  /**
   * Геометрия, снятая контроллером ИЗ terra-draw в момент закрытия
   * (`takeFinalGeometry`). Синхронная и потому главная.
   */
  finalGeometry: EditorGeometry | null;
  /** Фолбэк: последняя геометрия из отложенного колбэка `onGeometryChange`. */
  refGeometry: EditorGeometry | null;
  /** Фолбэк: последнее кольцо из отложенного колбэка `onRingChange`. */
  refRing: LngLat[] | null;
  /** Режим «+ Поле» включён. */
  addFieldActive: boolean;
  /** Фаза режима «+ Поле» — решает судьбу ПУСТОГО выхода, и только её. */
  addFieldPhase: AddFieldPhase;
  /**
   * AK-591: в этой сессии редактора загружался контур СУЩЕСТВУЮЩЕГО поля
   * (traceField по клику на поле). Такой контур наружу не уходит никогда —
   * иначе «Обвести поле» → «Готово» создавало бы дубль чужого поля.
   */
  tracedExistingContour: boolean;
  /** Пользователь вправе создавать поля (structure:c). */
  canCreateField: boolean;
}

export type ReturnFromEditorAction =
  /** Ничего не делаем: редактор был не наш либо отдавать нечего. */
  | { type: 'noop' }
  /** Уточнённый контур → в строку импорта. */
  | { type: 'import-apply'; index: number; geometry: { type: 'Polygon'; coordinates: LngLat[][] } }
  /** Правку строки импорта отменяем (контура нет). */
  | { type: 'import-cancel' }
  /** Контур → в форму сохранения нового поля. */
  | { type: 'add-field-apply'; geometry: EditorGeometry }
  /** Рисование прервали без контура → назад в armed текущего способа. */
  | { type: 'add-field-reset' }
  /**
   * AK-591 (репро прод v1.1608, пустой тенант): человек нарисовал контур в
   * БЕЗВЛАДЕЛЬНОМ редакторе («Обвести поле» — там есть инструменты
   * круг/прямоугольник/полигон) и нажал «Готово». Раньше — noop: нарисованное
   * молча выбрасывалось, формы не было, ноль POST /fields. Работу человека не
   * теряем: предлагаем сохранить как НОВОЕ поле (включить «+ Поле» и открыть
   * форму с этой геометрией).
   */
  | { type: 'draw-save-as-new'; geometry: EditorGeometry };

/** Внешнее кольцо геометрии (первая часть), если оно пригодно как контур. */
function outerRing(geometry: EditorGeometry | null): LngLat[] | null {
  if (!geometry) return null;
  const ring =
    geometry.type === 'Polygon'
      ? (geometry.coordinates[0] as LngLat[] | undefined)
      : (geometry.coordinates[0]?.[0] as LngLat[] | undefined);
  return ring && ring.length >= 4 ? ring : null;
}

/**
 * Что делать, когда редактор контура закрылся.
 *
 * Порядок источников геометрии: снимок контроллера → реф полной геометрии →
 * реф кольца. Первый синхронный, остальные приходят отложенными колбэками
 * (recompute идёт через requestAnimationFrame) и потому только страхуют случай,
 * когда контроллер пересоздали между рисованием и закрытием.
 */
export function decideReturnFromEditor(input: ReturnFromEditorInput): ReturnFromEditorAction {
  const ringGeometry: EditorGeometry | null =
    input.refRing && input.refRing.length >= 4
      ? { type: 'Polygon', coordinates: [input.refRing] }
      : null;
  const geometry = input.finalGeometry ?? input.refGeometry ?? ringGeometry;

  // Явная «Отмена» режима «+ Поле» — черновик выброшен намеренно, наружу не
  // уходит НИЧЕГО (до ветки draw-save-as-new: иначе отменённый контур
  // воскресал бы формой сохранения — блокер ревью архитектора по AK-591).
  if (input.owner === 'discard') return { type: 'noop' };

  if (input.owner === 'import') {
    if (input.importIndex === null) return { type: 'noop' };
    const ring = outerRing(geometry);
    return ring
      ? { type: 'import-apply', index: input.importIndex, geometry: { type: 'Polygon', coordinates: [ring] } }
      : { type: 'import-cancel' };
  }

  // Редактор открывал не режим «+ Поле». Если сессия трогала контур
  // СУЩЕСТВУЮЩЕГО поля («Обвести поле» по клику) — наружу не уходит ничего
  // (см. шапку модуля: иначе дубль чужого поля одним кликом). Но если человек
  // РИСОВАЛ С НУЛЯ (существующий контур не загружался) — это его работа, и
  // «Готово» не имеет права её молча выбросить (AK-591): предлагаем сохранить
  // как новое поле, если прав хватает.
  if (input.owner !== 'add-field') {
    if (
      input.owner === null &&
      geometry &&
      !input.tracedExistingContour &&
      input.canCreateField
    ) {
      return { type: 'draw-save-as-new', geometry };
    }
    return { type: 'noop' };
  }
  if (!input.addFieldActive) return { type: 'noop' };

  if (geometry) return { type: 'add-field-apply', geometry };

  // Фаза решает ТОЛЬКО судьбу пустого выхода: рисовали/правили и вышли ни с чем
  // → назад в armed. В любой другой фазе пустой выход — просто ничего.
  if (input.addFieldPhase === 'editing' || input.addFieldPhase === 'drawing') {
    return { type: 'add-field-reset' };
  }
  return { type: 'noop' };
}
